export type Language = 'en' | 'fr' | 'ar';

export interface Translations {
  nav: {
    about: string;
    pricing: string;
    faq: string;
    startFlowing: string;
    signIn: string;
  };
  hero: {
    seoTitle: string;
    title: {
      line1: string;
      line2: string;
    };
    subtitle: string;
    cta: string;
    tagline: string;
  };
  problem: {
    title: string;
    subtitle: string;
    cards: Array<{
      title: string;
      description: string;
    }>;
  };
  solution: {
    title: string;
    subtitle: string;
    features: Array<{
      title: string;
      tagline: string;
      description: string;
      bullets: string[];
    }>;
  };
  howItWorks: {
    title: string;
    steps: Array<{
      title: string;
      description: string;
    }>;
  };
  whyPravos: {
    title: string;
    tableHeaders: {
      competitor: string;
      price: string;
      problems: string;
      solutions: string;
    };
    comparisons: Array<{
      competitor: string;
      competitorPrice: string;
      problems: string[];
      solutions: string[];
    }>;
    pricing: {
      intro: string;
      price: string;
      perMonth: string;
      tagline: string;
    };
  };
  pricing: {
    title: string;
    subtitle: string;
    free: {
      name: string;
      price: string;
      period: string;
      features: string[];
      cta: string;
    };
    pro: {
      badge: string;
      name: string;
      price: string;
      period: string;
      yearlyPrice: string;
      intro: string;
      features: string[];
      cta: string;
      noCreditCard: string;
    };
  };
  faq: {
    title: string;
    questions: Array<{
      question: string;
      answer: string;
    }>;
  };
  cta: {
    title: {
      part1: string;
      part2: string;
    };
    subtitle: string;
    cta: string;
    watchDemo: string;
    demoModal: {
      title: string;
      description: string;
      comingSoon: string;
    };
  };
  footer: {
    tagline: string;
    flowState: string;
    etymology: string;
    sections: {
      product: {
        title: string;
        links: {
          about: string;
          pricing: string;
          faq: string;
          blog: string;
          roadmap: string;
        };
      };
      legal: {
        title: string;
        links: {
          privacy: string;
          terms: string;
          refund: string;
        };
      };
      contact: {
        title: string;
        links: {
          support: string;
          press: string;
          partnerships: string;
        };
      };
    };
    copyright: string;
  };
}

export const translations: Record<Language, Translations> = {
  en: {
    nav: {
      about: 'About',
      pricing: 'Pricing',
      faq: 'FAQ',
      startFlowing: 'Start flowing',
      signIn: 'Sign in',
    },
    hero: {
      seoTitle:
        'Pravos - Pomodoro Timer, Task Manager & Focus Music App for Deep Work',
      title: {
        line1: 'Stop Fighting Distraction.',
        line2: 'Start Flowing.',
      },
      subtitle:
        'Pravos is a productivity app that helps you focus and get deep work done faster. Replace your Pomodoro timer, task list, and music app with one beautiful workspace.',
      cta: 'Start Flowing',
      tagline: 'Built by humans who love flow as much as you do.',
    },
    problem: {
      title: "Focus Shouldn't Require Eight Open Tabs",
      subtitle: "We're looking at you Todoist, Spotify & Brain.fm",
      cards: [
        {
          title: 'Tool Fragmentation Kills Flow',
          description:
            "You've got Spotify in one tab, Todoist in another, a Pomodoro timer somewhere else, and your calendar demanding attention. Every context switch pulls you out of focus. Your brain never settles.",
        },
        {
          title: 'Focus Apps Collect Dust',
          description:
            "You download the app with good intentions. Use it twice. Forget it exists.Most focus apps fail because they don't create sustainable habits—they just add to your app graveyard.",
        },
        {
          title: 'Music That All Sounds the Same',
          description:
            "BrainFM and Endel promise endless variety. Two months later, you've heard it all. The soundscapes all blur together. You've payed $90 a year for background noise and they won't refund you a dime.",
        },
      ],
    },
    solution: {
      title: 'One Workspace with Timer, Tasks & Music.',
      subtitle:
        'Pravos is an operating system for deep work that combines decades of experience in behavior science and product design to help you prioritize, focus and get work done faster.',
      features: [
        {
          title: 'Focus Workspace',
          tagline: 'Everything in one place, finally.',
          description:
            'Replace your Pomodoro timer, Todoist, Spotify, and calendar—all in one customizable workspace. Stop juggling tabs and enter flow. No more context-switching between fragmented tools.',
          bullets: [
            'Minimalist dark-mode design',
            'Distraction-free interface',
            'Keyboard shortcuts for power users',
            'Saves your layout & preferences automatically',
          ],
        },
        {
          title: 'Music Composed by Focus Artists',
          tagline:
            'Get into flow with music specifically designed for Pomodoro-style focus sessions.',
          description:
            "Unlike Brain.fm's AI-generated soundscapes or Endel's algorithmic loops, we partner with real independent artists to create a wide-range of 25-minute focus music albums. Fresh, human-made audio for writing, coding, designing, or powering through your inbox.",
          bullets: [
            'New focus music albums dropped every week',
            'Each album is a unique 25-minute focus journey',
            'Focus music genres for every kind of task',
            'High-quality lossless audio (Pro)',
            'Download for offline sessions (Pro)',
          ],
        },
        {
          title: 'Designed for ADHD & Neurodivergent Brains',
          tagline: 'Built for real humans, not productivity robots.',
          description:
            'We design for modern, distractible, dopamine-seeking brains that crave novelty and reward. Immediate visual feedback. Progress tracking. Session streaks. Focus that actually works for you.',
          bullets: [
            'Minimalist design and functionality',
            'Visual progress indicators',
            'Focus and task completion insights',
            'Flexible session lengths (Pro)',
          ],
        },
      ],
    },
    howItWorks: {
      title: 'From Distracted to Flow State in 60 Seconds',
      steps: [
        {
          title: 'Add tasks & pick your playlist',
          description: 'Add your tasks, and choose your music to get into flow.',
        },
        {
          title: 'Start Your Flow',
          description: 'Hit "Play". Timer starts. Music flows. Get work done.',
        },
        {
          title: 'Track Your Progress',
          description:
            'Complete sessions. Build streaks. See how your time is invested across tasks.',
        },
      ],
    },
    whyPravos: {
      title: 'Why Choose Pravos Over Other Focus Apps?',
      tableHeaders: {
        competitor: "What You're Using Now",
        price: 'Their Price',
        problems: "Why It's Not Working",
        solutions: 'Pravos Solution',
      },
      comparisons: [
        {
          competitor: 'Brain.fm',
          competitorPrice: '$9.99/mo',
          problems: [
            'Annoying wobbly audio',
            'No integration with tasks',
            'Overstated science claims',
          ],
          solutions: [
            'Focus music curated by artists',
            'Everything in one workspace',
            'See results yourself',
          ],
        },
        {
          competitor: 'Endel',
          competitorPrice: '$19.99/mo',
          problems: [
            'Generative AI audio gets boring',
            'No task management',
            'Feels generic and repetitive',
          ],
          solutions: [
            'Tailor-made music with intention',
            'Full workflow integration',
            'Curated for deep work',
          ],
        },
        {
          competitor: 'Be Focused Pro + Spotify + Todoist',
          competitorPrice: '$16.99/mo',
          problems: [
            'Context-switching destroys focus',
            'Managing 3 separate apps',
            'Clunky workflow',
          ],
          solutions: [
            'Everything in one workspace',
            'Seamless task-to-music flow',
            'No app switching needed',
          ],
        },
        {
          competitor: 'Pure Willpower',
          competitorPrice: 'Free (but costly)',
          problems: [
            'Burnout city',
            'Unsustainable long-term',
            'No structure or support',
          ],
          solutions: [
            'Create conditions for flow',
            'Sustainable productivity',
            'Science-backed structure',
          ],
        },
      ],
      pricing: {
        intro: 'All this for just',
        price: '$2.99',
        perMonth: '/ mo',
        tagline: 'Less than a coffee. Cancel anytime. 14-day free trial.',
      },
    },
    pricing: {
      title: 'Generous Free Tier. Honest Premium. No Hidden Costs.',
      subtitle:
        "No surprise charges. We'll remind you before your renewal. Cancel easily from profile settings.",
      free: {
        name: 'Pravos Flow',
        price: '$0',
        period: '/month forever',
        features: [
          '3 focus sessions per day',
          'Basic timer (Pomodoro)',
          'Simple To-do list widget',
          'Basic focus playlists',
          'Basic progress tracking',
          'One custom workspace',
        ],
        cta: 'Start Free',
      },
      pro: {
        badge: 'Most Popular',
        name: 'Pravos Pro',
        price: '$2.99',
        period: '/month',
        yearlyPrice: 'or $28/year (save 22%)',
        intro: 'Everything in Free, plus:',
        features: [
          'Unlimited focus sessions',
          'Premium focus music library',
          'Advanced task management system',
          'High-fidelity audio (lossless WAV)',
          'Full analytics (productivity insights)',
          'Priority support',
        ],
        cta: 'Try Pro Free for 14 Days',
        noCreditCard: 'No credit card required',
      },
    },
    faq: {
      title: "Questions? We've Got Answers.",
      questions: [
        {
          question: 'What does "pravos" mean?',
          answer:
            'Pravos comes from the Sanskrit word meaning "flow" or "current." It represents the natural state of effortless focus we help you achieve.',
        },
        {
          question: 'How is this different from Brain.fm or Endel?',
          answer:
            "Pravos is a complete productivity operating system, not just an audio app. We combine focus audio with timers, task management, and workspace customization. Plus, we partner with real artists for fresh content, not just AI-generated soundscapes.",
        },
        {
          question: 'Is the free tier actually free forever?',
          answer:
            'Yes! The free tier includes core Pomodoro timer, basic task list, and curated focus playlists. No credit card required. You can upgrade to Pro for advanced features like custom session lengths, AI-curated playlists, and offline downloads.',
        },
        {
          question: 'Will this work for my ADHD brain?',
          answer:
            'Absolutely. Pravos was designed with neurodiversity in mind. Visual progress indicators, immediate feedback, gamification elements, and flexible session lengths help ADHD brains stay engaged without feeling restricted.',
        },
        {
          question: 'What is the Pomodoro Technique?',
          answer:
            'The Pomodoro Technique is a time management method where you work in focused 25-minute intervals (called "Pomodoros") followed by short 5-minute breaks. After 4 Pomodoros, you take a longer 15-30 minute break. Pravos automates this entire workflow with built-in timers, progress tracking, and focus music that adapts to each phase of your session.',
        },
        {
          question: 'What music genres does Pravos offer?',
          answer:
            'Pravos partners with independent artists to create diverse focus music across multiple genres: Future Garage & Chillstep (Neural Drift, Cognitive Bloom, Vibe Coding), Ambient Piano & Lo-Fi (Scripted Light, Soulful Lounge), Felt Piano & Minimalist Classical (Deep Piano Focus, Relaxed Classical), Sacred Ambient (Meditative Ambient), and Sufi Lo-Fi with traditional instruments. Each album is a unique 25-minute focus journey designed specifically for Pomodoro sessions. New albums added weekly.',
        },
        {
          question: 'Can I use Pravos offline?',
          answer:
            'The web app currently requires an internet connection for full functionality. However, we\'re actively working on offline mode for Pro users, which will allow you to download focus music albums and access your task list without connectivity. Offline mode is coming soon. Native mobile apps with full offline support are also in development.',
        },
        {
          question: 'Does Pravos integrate with other productivity apps?',
          answer:
            'Currently, Pravos works as a standalone workspace designed to replace multiple fragmented tools. We\'re building integrations with popular productivity apps including Notion, Todoist, and Google Calendar—coming soon. Our philosophy is "everything in one place," but we understand some workflows require bridging tools.',
        },
        {
          question: "What's your refund policy?",
          answer:
            'We offer a 30-day money-back guarantee on all Pro subscriptions. If Pravos doesn\'t help you focus better, we\'ll refund you—no questions asked.',
        },
        {
          question: 'How much does Pravos cost?',
          answer:
            'Pravos offers a generous free tier forever with core features: 3 focus sessions per day, basic Pomodoro timer, simple task list, and access to basic focus playlists. Pro plans start at $2.99/month or $28/year (save 22%). Pro includes unlimited sessions, premium music library, high-fidelity audio (lossless WAV), advanced task management, full analytics, and priority support. No surprise charges—we remind you before renewal.',
        },
        {
          question: 'Is there a mobile app?',
          answer:
            'Currently Pravos is available as a web app that works on all devices. Native iOS and Android apps are in development and coming soon!',
        },
      ],
    },
    cta: {
      title: {
        part1: 'One workspace.',
        part2: 'Pure flow.',
      },
      subtitle:
        'Everything you need to focus in one beautiful workspace: Timer, tasks, and music. No more scattered apps. No more distracted brain.',
      cta: 'Start flowing',
      watchDemo: 'Watch Demo',
      demoModal: {
        title: '60-Second Tour',
        description:
          'See how Pravos helps you achieve deep focus in under a minute.',
        comingSoon: 'Demo video coming soon!',
      },
    },
    footer: {
      tagline: 'Pravos',
      flowState: 'Flow State Operating System',
      etymology: 'From Sanskrit pravāha: continuous flow',
      sections: {
        product: {
          title: 'Product',
          links: {
            about: 'About',
            pricing: 'Pricing',
            faq: 'FAQ',
            blog: 'Blog',
            roadmap: 'Roadmap',
          },
        },
        legal: {
          title: 'Legal',
          links: {
            privacy: 'Privacy Policy',
            terms: 'Terms of Service',
            refund: 'Refund Policy',
          },
        },
        contact: {
          title: 'Contact',
          links: {
            support: 'Support',
            press: 'Press',
            partnerships: 'Partnerships',
          },
        },
      },
      copyright: '© 2025 Pravos. All rights reserved.',
    },
  },
  fr: {
    nav: {
      about: 'À propos',
      pricing: 'Tarifs',
      faq: 'FAQ',
      startFlowing: 'Commencer',
      signIn: 'Se connecter',
    },
    hero: {
      seoTitle:
        'Pravos - Minuteur Pomodoro, Gestionnaire de Tâches et Application de Musique de Concentration pour le Deep Work',
      title: {
        line1: 'Arrêtez de Combattre la Distraction.',
        line2: 'Commencez à Vous Concentrer.',
      },
      subtitle:
        "Pravos est une application de productivité qui vous aide à vous concentrer et à accomplir votre travail profond plus rapidement. Remplacez votre minuteur Pomodoro, votre liste de tâches et votre application musicale par un seul espace de travail magnifique.",
      cta: 'Commencer',
      tagline: 'Créé par des humains qui aiment le flow autant que vous.',
    },
    problem: {
      title: 'La Concentration ne Devrait pas Nécessiter Huit Onglets Ouverts',
      subtitle: 'On vous regarde Todoist, Spotify & Brain.fm',
      cards: [
        {
          title: 'La Fragmentation des Outils Tue le Flow',
          description:
            'Vous avez Spotify dans un onglet, Todoist dans un autre, un minuteur Pomodoro ailleurs, et votre calendrier qui réclame de l\'attention. Chaque changement de contexte vous sort de votre concentration. Votre cerveau ne se pose jamais.',
        },
        {
          title: 'Les Applications de Concentration Prennent la Poussière',
          description:
            'Vous téléchargez l\'application avec de bonnes intentions. Vous l\'utilisez deux fois. Vous oubliez qu\'elle existe. La plupart des applications de concentration échouent parce qu\'elles ne créent pas d\'habitudes durables—elles ne font qu\'ajouter à votre cimetière d\'applications.',
        },
        {
          title: 'Une Musique qui Sonne Toujours Pareil',
          description:
            'BrainFM et Endel promettent une variété infinie. Deux mois plus tard, vous avez tout entendu. Les paysages sonores se confondent tous. Vous avez payé 90$ par an pour du bruit de fond et ils ne vous rembourseront pas un centime.',
        },
      ],
    },
    solution: {
      title: 'Un Seul Espace de Travail avec Minuteur, Tâches et Musique.',
      subtitle:
        'Pravos est un système d\'exploitation pour le travail profond qui combine des décennies d\'expérience en science comportementale et en conception de produits pour vous aider à prioriser, vous concentrer et accomplir votre travail plus rapidement.',
      features: [
        {
          title: 'Espace de Travail Concentré',
          tagline: 'Tout au même endroit, enfin.',
          description:
            'Remplacez votre minuteur Pomodoro, Todoist, Spotify et calendrier—tout dans un seul espace de travail personnalisable. Arrêtez de jongler entre les onglets et entrez en flow. Plus de changement de contexte entre des outils fragmentés.',
          bullets: [
            'Design minimaliste en mode sombre',
            'Interface sans distraction',
            'Raccourcis clavier pour les utilisateurs avancés',
            'Sauvegarde automatique de votre disposition et préférences',
          ],
        },
        {
          title: 'Musique Composée par des Artistes de Concentration',
          tagline:
            'Entrez en flow avec de la musique spécialement conçue pour les sessions de concentration de type Pomodoro.',
          description:
            'Contrairement aux paysages sonores générés par IA de Brain.fm ou aux boucles algorithmiques d\'Endel, nous nous associons avec de vrais artistes indépendants pour créer une large gamme d\'albums de musique de concentration de 25 minutes. Audio frais et fait par des humains pour écrire, coder, concevoir ou venir à bout de votre boîte mail.',
          bullets: [
            'Nouveaux albums de musique de concentration chaque semaine',
            'Chaque album est un parcours de concentration unique de 25 minutes',
            'Genres de musique de concentration pour chaque type de tâche',
            'Audio haute qualité sans perte (Pro)',
            'Téléchargement pour sessions hors ligne (Pro)',
          ],
        },
        {
          title: 'Conçu pour les Cerveaux TDAH et Neuroatypiques',
          tagline:
            'Construit pour de vrais humains, pas des robots de productivité.',
          description:
            'Nous concevons pour des cerveaux modernes, distractibles, en quête de dopamine qui ont besoin de nouveauté et de récompense. Retour visuel immédiat. Suivi de progression. Séries de sessions. Une concentration qui fonctionne vraiment pour vous.',
          bullets: [
            'Design et fonctionnalités minimalistes',
            'Indicateurs visuels de progression',
            'Insights de concentration et d\'achèvement des tâches',
            'Durées de session flexibles (Pro)',
          ],
        },
      ],
    },
    howItWorks: {
      title: 'De la Distraction à l\'État de Flow en 60 Secondes',
      steps: [
        {
          title: 'Ajoutez des tâches et choisissez votre playlist',
          description:
            'Ajoutez vos tâches et choisissez votre musique pour entrer en flow.',
        },
        {
          title: 'Démarrez Votre Flow',
          description:
            'Appuyez sur "Play". Le minuteur démarre. La musique coule. Accomplissez votre travail.',
        },
        {
          title: 'Suivez Votre Progression',
          description:
            'Complétez des sessions. Construisez des séries. Voyez comment votre temps est investi à travers vos tâches.',
        },
      ],
    },
    whyPravos: {
      title: 'Pourquoi Choisir Pravos Plutôt que d\'Autres Applications de Concentration ?',
      tableHeaders: {
        competitor: 'Ce que Vous Utilisez Maintenant',
        price: 'Leur Prix',
        problems: 'Pourquoi Ça ne Marche Pas',
        solutions: 'Solution Pravos',
      },
      comparisons: [
        {
          competitor: 'Brain.fm',
          competitorPrice: '9,99$/mois',
          problems: [
            'Audio oscillant ennuyeux',
            'Pas d\'intégration avec les tâches',
            'Affirmations scientifiques exagérées',
          ],
          solutions: [
            'Musique de concentration sélectionnée par des artistes',
            'Tout dans un seul espace de travail',
            'Voyez les résultats par vous-même',
          ],
        },
        {
          competitor: 'Endel',
          competitorPrice: '19,99$/mois',
          problems: [
            'L\'audio IA générative devient ennuyeux',
            'Pas de gestion des tâches',
            'Semble générique et répétitif',
          ],
          solutions: [
            'Musique sur mesure avec intention',
            'Intégration complète du flux de travail',
            'Sélectionné pour le travail profond',
          ],
        },
        {
          competitor: 'Be Focused Pro + Spotify + Todoist',
          competitorPrice: '16,99$/mois',
          problems: [
            'Le changement de contexte détruit la concentration',
            'Gestion de 3 applications séparées',
            'Flux de travail maladroit',
          ],
          solutions: [
            'Tout dans un seul espace de travail',
            'Flux transparent tâche-musique',
            'Pas besoin de changer d\'application',
          ],
        },
        {
          competitor: 'Pure Force de Volonté',
          competitorPrice: 'Gratuit (mais coûteux)',
          problems: [
            'Ville de l\'épuisement',
            'Insoutenable à long terme',
            'Pas de structure ou de soutien',
          ],
          solutions: [
            'Créer les conditions du flow',
            'Productivité durable',
            'Structure scientifiquement prouvée',
          ],
        },
      ],
      pricing: {
        intro: 'Tout ça pour seulement',
        price: '2,99$',
        perMonth: '/ mois',
        tagline: 'Moins qu\'un café. Annulez à tout moment. Essai gratuit de 14 jours.',
      },
    },
    pricing: {
      title: 'Niveau Gratuit Généreux. Premium Honnête. Pas de Frais Cachés.',
      subtitle:
        'Pas de frais surprise. Nous vous rappellerons avant votre renouvellement. Annulez facilement depuis les paramètres de profil.',
      free: {
        name: 'Pravos Flow',
        price: '0$',
        period: '/mois pour toujours',
        features: [
          '3 sessions de concentration par jour',
          'Minuteur basique (Pomodoro)',
          'Widget de liste de tâches simple',
          'Playlists de concentration basiques',
          'Suivi de progression basique',
          'Un espace de travail personnalisé',
        ],
        cta: 'Commencer Gratuitement',
      },
      pro: {
        badge: 'Le Plus Populaire',
        name: 'Pravos Pro',
        price: '2,99$',
        period: '/mois',
        yearlyPrice: 'ou 28$/an (économisez 22%)',
        intro: 'Tout ce qui est dans Gratuit, plus :',
        features: [
          'Sessions de concentration illimitées',
          'Bibliothèque de musique de concentration premium',
          'Système de gestion des tâches avancé',
          'Audio haute fidélité (WAV sans perte)',
          'Analytique complète (insights de productivité)',
          'Support prioritaire',
        ],
        cta: 'Essayez Pro Gratuitement pendant 14 Jours',
        noCreditCard: 'Aucune carte de crédit requise',
      },
    },
    faq: {
      title: 'Des Questions ? Nous Avons les Réponses.',
      questions: [
        {
          question: 'Que signifie "pravos" ?',
          answer:
            'Pravos vient du mot sanskrit signifiant "flux" ou "courant". Il représente l\'état naturel de concentration sans effort que nous vous aidons à atteindre.',
        },
        {
          question: 'En quoi est-ce différent de Brain.fm ou Endel ?',
          answer:
            'Pravos est un système d\'exploitation de productivité complet, pas seulement une application audio. Nous combinons l\'audio de concentration avec des minuteurs, la gestion des tâches et la personnalisation de l\'espace de travail. De plus, nous nous associons avec de vrais artistes pour du contenu frais, pas seulement des paysages sonores générés par IA.',
        },
        {
          question: 'Le niveau gratuit est-il vraiment gratuit pour toujours ?',
          answer:
            'Oui ! Le niveau gratuit inclut un minuteur Pomodoro de base, une liste de tâches basique et des playlists de concentration sélectionnées. Aucune carte de crédit requise. Vous pouvez passer à Pro pour des fonctionnalités avancées comme des durées de session personnalisées, des playlists sélectionnées par IA et des téléchargements hors ligne.',
        },
        {
          question: 'Est-ce que ça marchera pour mon cerveau TDAH ?',
          answer:
            'Absolument. Pravos a été conçu avec la neurodiversité à l\'esprit. Les indicateurs visuels de progression, le retour immédiat, les éléments de gamification et les durées de session flexibles aident les cerveaux TDAH à rester engagés sans se sentir restreints.',
        },
        {
          question: 'Qu\'est-ce que la Technique Pomodoro ?',
          answer:
            'La Technique Pomodoro est une méthode de gestion du temps où vous travaillez par intervalles de 25 minutes concentrés (appelés "Pomodoros") suivis de courtes pauses de 5 minutes. Après 4 Pomodoros, vous prenez une pause plus longue de 15-30 minutes. Pravos automatise tout ce flux de travail avec des minuteurs intégrés, un suivi de progression et une musique de concentration qui s\'adapte à chaque phase de votre session.',
        },
        {
          question: 'Quels genres de musique Pravos propose-t-il ?',
          answer:
            'Pravos s\'associe avec des artistes indépendants pour créer une musique de concentration diversifiée à travers plusieurs genres : Future Garage & Chillstep (Neural Drift, Cognitive Bloom, Vibe Coding), Piano Ambient & Lo-Fi (Scripted Light, Soulful Lounge), Piano Felt & Classique Minimaliste (Deep Piano Focus, Relaxed Classical), Ambient Sacré (Meditative Ambient), et Lo-Fi Soufi avec instruments traditionnels. Chaque album est un parcours de concentration unique de 25 minutes conçu spécifiquement pour les sessions Pomodoro. Nouveaux albums ajoutés chaque semaine.',
        },
        {
          question: 'Puis-je utiliser Pravos hors ligne ?',
          answer:
            'L\'application web nécessite actuellement une connexion Internet pour une fonctionnalité complète. Cependant, nous travaillons activement sur un mode hors ligne pour les utilisateurs Pro, qui vous permettra de télécharger des albums de musique de concentration et d\'accéder à votre liste de tâches sans connectivité. Le mode hors ligne arrive bientôt. Les applications mobiles natives avec support hors ligne complet sont également en développement.',
        },
        {
          question: 'Pravos s\'intègre-t-il avec d\'autres applications de productivité ?',
          answer:
            'Actuellement, Pravos fonctionne comme un espace de travail autonome conçu pour remplacer plusieurs outils fragmentés. Nous construisons des intégrations avec des applications de productivité populaires incluant Notion, Todoist et Google Calendar—à venir bientôt. Notre philosophie est "tout au même endroit", mais nous comprenons que certains flux de travail nécessitent de relier des outils.',
        },
        {
          question: 'Quelle est votre politique de remboursement ?',
          answer:
            'Nous offrons une garantie de remboursement de 30 jours sur tous les abonnements Pro. Si Pravos ne vous aide pas à mieux vous concentrer, nous vous rembourserons—sans poser de questions.',
        },
        {
          question: 'Combien coûte Pravos ?',
          answer:
            'Pravos offre un niveau gratuit généreux pour toujours avec des fonctionnalités de base : 3 sessions de concentration par jour, minuteur Pomodoro basique, liste de tâches simple et accès aux playlists de concentration basiques. Les forfaits Pro commencent à 2,99$/mois ou 28$/an (économisez 22%). Pro inclut des sessions illimitées, une bibliothèque de musique premium, de l\'audio haute fidélité (WAV sans perte), une gestion avancée des tâches, une analytique complète et un support prioritaire. Pas de frais surprise—nous vous rappelons avant le renouvellement.',
        },
        {
          question: 'Y a-t-il une application mobile ?',
          answer:
            'Actuellement, Pravos est disponible en tant qu\'application web qui fonctionne sur tous les appareils. Les applications natives iOS et Android sont en développement et arrivent bientôt !',
        },
      ],
    },
    cta: {
      title: {
        part1: 'Un seul espace de travail.',
        part2: 'Flow pur.',
      },
      subtitle:
        'Tout ce dont vous avez besoin pour vous concentrer dans un seul espace de travail magnifique : Minuteur, tâches et musique. Plus d\'applications dispersées. Plus de cerveau distrait.',
      cta: 'Commencer',
      watchDemo: 'Voir la Démo',
      demoModal: {
        title: 'Visite de 60 Secondes',
        description:
          'Voyez comment Pravos vous aide à atteindre une concentration profonde en moins d\'une minute.',
        comingSoon: 'Vidéo de démonstration à venir !',
      },
    },
    footer: {
      tagline: 'Pravos',
      flowState: 'Système d\'Exploitation pour l\'État de Flow',
      etymology: 'Du sanskrit pravāha : flux continu',
      sections: {
        product: {
          title: 'Produit',
          links: {
            about: 'À propos',
            pricing: 'Tarifs',
            faq: 'FAQ',
            blog: 'Blog',
            roadmap: 'Feuille de route',
          },
        },
        legal: {
          title: 'Légal',
          links: {
            privacy: 'Politique de Confidentialité',
            terms: 'Conditions d\'Utilisation',
            refund: 'Politique de Remboursement',
          },
        },
        contact: {
          title: 'Contact',
          links: {
            support: 'Support',
            press: 'Presse',
            partnerships: 'Partenariats',
          },
        },
      },
      copyright: '© 2025 Pravos. Tous droits réservés.',
    },
  },
  ar: {
    nav: {
      about: 'عن البرنامج',
      pricing: 'الأسعار',
      faq: 'الأسئلة الشائعة',
      startFlowing: 'ابدأ التركيز',
      signIn: 'تسجيل الدخول',
    },
    hero: {
      seoTitle:
        'برافوس - مؤقت بومودورو، مدير المهام وتطبيق موسيقى التركيز للعمل العميق',
      title: {
        line1: 'توقف عن محاربة التشتت.',
        line2: 'ابدأ التركيز.',
      },
      subtitle:
        'برافوس هو تطبيق إنتاجية يساعدك على التركيز وإنجاز العمل العميق بشكل أسرع. استبدل مؤقت بومودورو، قائمة المهام، وتطبيق الموسيقى بمساحة عمل واحدة جميلة.',
      cta: 'ابدأ الآن',
      tagline: 'صُنع بواسطة بشر يحبون التركيز مثلك تمامًا.',
    },
    problem: {
      title: 'التركيز لا يجب أن يتطلب ثمانية تبويبات مفتوحة',
      subtitle: 'ننظر إليك يا تودويست، سبوتيفاي وبراين.اف.ام',
      cards: [
        {
          title: 'تجزئة الأدوات تقتل التركيز',
          description:
            'لديك سبوتيفاي في تبويب واحد، تودويست في آخر، مؤقت بومودورو في مكان ما، والتقويم يطلب الانتباه. كل تبديل سياق يخرجك من التركيز. عقلك لا يستقر أبدًا.',
        },
        {
          title: 'تطبيقات التركيز تجمع الغبار',
          description:
            'تنزل التطبيق بنوايا حسنة. تستخدمه مرتين. تنسى أنه موجود. معظم تطبيقات التركيز تفشل لأنها لا تنشئ عادات مستدامة—بل تضيف إلى مقبرة تطبيقاتك فقط.',
        },
        {
          title: 'موسيقى تبدو كلها متشابهة',
          description:
            'براين.اف.ام وإنديل يعدان بتنوع لا نهائي. بعد شهرين، سمعت كل شيء. المناظر الصوتية كلها تختلط. دفعت 90 دولارًا في السنة لضوضاء خلفية ولن يعيدوا لك سنتًا واحدًا.',
        },
      ],
    },
    solution: {
      title: 'مساحة عمل واحدة مع المؤقت والمهام والموسيقى.',
      subtitle:
        'برافوس هو نظام تشغيل للعمل العميق يجمع عقودًا من الخبرة في علوم السلوك وتصميم المنتجات لمساعدتك على تحديد الأولويات والتركيز وإنجاز العمل بشكل أسرع.',
      features: [
        {
          title: 'مساحة عمل التركيز',
          tagline: 'كل شيء في مكان واحد، أخيرًا.',
          description:
            'استبدل مؤقت بومودورو، تودويست، سبوتيفاي، والتقويم—كلها في مساحة عمل واحدة قابلة للتخصيص. توقف عن التنقل بين التبويبات وادخل في التركيز. لا مزيد من تبديل السياق بين الأدوات المجزأة.',
          bullets: [
            'تصميم بسيط بالوضع المظلم',
            'واجهة خالية من التشتت',
            'اختصارات لوحة المفاتيح للمستخدمين المتقدمين',
            'يحفظ تخطيطك وتفضيلاتك تلقائيًا',
          ],
        },
        {
          title: 'موسيقى مؤلفة بواسطة فناني التركيز',
          tagline:
            'ادخل في التركيز مع موسيقى مصممة خصيصًا لجلسات التركيز على طريقة بومودورو.',
          description:
            'على عكس المناظر الصوتية المولدة بالذكاء الاصطناعي من براين.اف.ام أو الحلقات الخوارزمية من إنديل، نتعاون مع فنانين مستقلين حقيقيين لإنشاء مجموعة واسعة من ألبومات موسيقى التركيز لمدة 25 دقيقة. صوت طازج ومصنوع بواسطة البشر للكتابة أو البرمجة أو التصميم أو التعامل مع بريدك الوارد.',
          bullets: [
            'ألبومات موسيقى تركيز جديدة كل أسبوع',
            'كل ألبوم رحلة تركيز فريدة لمدة 25 دقيقة',
            'أنواع موسيقى التركيز لكل نوع من المهام',
            'صوت عالي الجودة بدون فقدان (برو)',
            'تنزيل للجلسات دون اتصال (برو)',
          ],
        },
        {
          title: 'مصمم لعقول اضطراب فرط الحركة ونقص الانتباه والعقول العصبية المتنوعة',
          tagline: 'مصنوع للبشر الحقيقيين، وليس لروبوتات الإنتاجية.',
          description:
            'نصمم للعقول الحديثة القابلة للتشتت والباحثة عن الدوبامين التي تتوق للجدة والمكافأة. ردود فعل بصرية فورية. تتبع التقدم. سلاسل الجلسات. تركيز يعمل فعلاً من أجلك.',
          bullets: [
            'تصميم ووظائف بسيطة',
            'مؤشرات تقدم بصرية',
            'رؤى التركيز وإكمال المهام',
            'أطوال جلسات مرنة (برو)',
          ],
        },
      ],
    },
    howItWorks: {
      title: 'من التشتت إلى حالة التركيز في 60 ثانية',
      steps: [
        {
          title: 'أضف المهام واختر قائمة التشغيل',
          description: 'أضف مهامك، واختر موسيقاك للدخول في التركيز.',
        },
        {
          title: 'ابدأ تركيزك',
          description: 'اضغط "تشغيل". يبدأ المؤقت. تتدفق الموسيقى. أنجز العمل.',
        },
        {
          title: 'تتبع تقدمك',
          description:
            'أكمل الجلسات. ابنِ السلاسل. شاهد كيف يُستثمر وقتك عبر المهام.',
        },
      ],
    },
    whyPravos: {
      title: 'لماذا تختار برافوس على تطبيقات التركيز الأخرى؟',
      tableHeaders: {
        competitor: 'ما تستخدمه الآن',
        price: 'سعرهم',
        problems: 'لماذا لا يعمل',
        solutions: 'حل برافوس',
      },
      comparisons: [
        {
          competitor: 'Brain.fm',
          competitorPrice: '9.99$/شهر',
          problems: [
            'صوت متذبذب مزعج',
            'لا تكامل مع المهام',
            'ادعاءات علمية مبالغ فيها',
          ],
          solutions: [
            'موسيقى تركيز منسقة بواسطة فنانين',
            'كل شيء في مساحة عمل واحدة',
            'شاهد النتائج بنفسك',
          ],
        },
        {
          competitor: 'Endel',
          competitorPrice: '19.99$/شهر',
          problems: [
            'الصوت المولد بالذكاء الاصطناعي يصبح مملًا',
            'لا إدارة للمهام',
            'يبدو عامًا ومتكررًا',
          ],
          solutions: [
            'موسيقى مصممة خصيصًا بنية',
            'تكامل كامل لسير العمل',
            'منسق للعمل العميق',
          ],
        },
        {
          competitor: 'Be Focused Pro + Spotify + Todoist',
          competitorPrice: '16.99$/شهر',
          problems: [
            'تبديل السياق يدمر التركيز',
            'إدارة 3 تطبيقات منفصلة',
            'سير عمل أخرق',
          ],
          solutions: [
            'كل شيء في مساحة عمل واحدة',
            'انتقال سلس من المهمة إلى الموسيقى',
            'لا حاجة لتبديل التطبيقات',
          ],
        },
        {
          competitor: 'قوة الإرادة النقية',
          competitorPrice: 'مجاني (لكنه مكلف)',
          problems: [
            'مدينة الإرهاق',
            'غير مستدام على المدى الطويل',
            'لا بنية أو دعم',
          ],
          solutions: [
            'إنشاء ظروف للتركيز',
            'إنتاجية مستدامة',
            'بنية مدعومة علميًا',
          ],
        },
      ],
      pricing: {
        intro: 'كل هذا مقابل',
        price: '2.99$',
        perMonth: '/ شهر فقط',
        tagline: 'أقل من قهوة. ألغِ في أي وقت. تجربة مجانية لمدة 14 يومًا.',
      },
    },
    pricing: {
      title: 'مستوى مجاني سخي. بريميوم صادق. لا تكاليف خفية.',
      subtitle:
        'لا رسوم مفاجئة. سنذكرك قبل التجديد. ألغِ بسهولة من إعدادات الملف الشخصي.',
      free: {
        name: 'برافوس فلو',
        price: '0$',
        period: '/شهر للأبد',
        features: [
          '3 جلسات تركيز يوميًا',
          'مؤقت أساسي (بومودورو)',
          'أداة قائمة مهام بسيطة',
          'قوائم تشغيل تركيز أساسية',
          'تتبع تقدم أساسي',
          'مساحة عمل مخصصة واحدة',
        ],
        cta: 'ابدأ مجانًا',
      },
      pro: {
        badge: 'الأكثر شعبية',
        name: 'برافوس برو',
        price: '2.99$',
        period: '/شهر',
        yearlyPrice: 'أو 28$/سنة (وفر 22%)',
        intro: 'كل ما في المجاني، بالإضافة إلى:',
        features: [
          'جلسات تركيز غير محدودة',
          'مكتبة موسيقى تركيز بريميوم',
          'نظام إدارة مهام متقدم',
          'صوت عالي الدقة (WAV بدون فقدان)',
          'تحليلات كاملة (رؤى الإنتاجية)',
          'دعم ذو أولوية',
        ],
        cta: 'جرب برو مجانًا لمدة 14 يومًا',
        noCreditCard: 'لا حاجة لبطاقة ائتمان',
      },
    },
    faq: {
      title: 'أسئلة؟ لدينا الإجابات.',
      questions: [
        {
          question: 'ماذا يعني "برافوس"؟',
          answer:
            'برافوس يأتي من الكلمة السنسكريتية التي تعني "التركيز" أو "التيار". إنه يمثل الحالة الطبيعية للتركيز السهل الذي نساعدك على تحقيقه.',
        },
        {
          question: 'كيف يختلف هذا عن Brain.fm أو Endel؟',
          answer:
            'برافوس هو نظام تشغيل إنتاجية كامل، وليس مجرد تطبيق صوتي. نجمع بين صوت التركيز والمؤقتات وإدارة المهام وتخصيص مساحة العمل. بالإضافة إلى ذلك، نتعاون مع فنانين حقيقيين لمحتوى طازج، وليس فقط مناظر صوتية مولدة بالذكاء الاصطناعي.',
        },
        {
          question: 'هل المستوى المجاني مجاني حقًا للأبد؟',
          answer:
            'نعم! يتضمن المستوى المجاني مؤقت بومودورو أساسي، قائمة مهام أساسية، وقوائم تشغيل تركيز منسقة. لا حاجة لبطاقة ائتمان. يمكنك الترقية إلى برو للميزات المتقدمة مثل أطوال الجلسات المخصصة، قوائم التشغيل المنسقة بالذكاء الاصطناعي، والتنزيلات دون اتصال.',
        },
        {
          question: 'هل سيعمل هذا لعقلي المصاب باضطراب فرط الحركة ونقص الانتباه؟',
          answer:
            'بالتأكيد. تم تصميم برافوس مع التنوع العصبي في الاعتبار. مؤشرات التقدم البصرية، الردود الفورية، عناصر اللعب، وأطوال الجلسات المرنة تساعد عقول اضطراب فرط الحركة ونقص الانتباه على البقاء منخرطة دون الشعور بالتقييد.',
        },
        {
          question: 'ما هي تقنية بومودورو؟',
          answer:
            'تقنية بومودورو هي طريقة لإدارة الوقت حيث تعمل في فترات مركزة مدتها 25 دقيقة (تسمى "بومودوروس") تليها فترات راحة قصيرة مدتها 5 دقائق. بعد 4 بومودوروس، تأخذ استراحة أطول من 15-30 دقيقة. يؤتمت برافوس سير العمل هذا بالكامل مع مؤقتات مدمجة، تتبع التقدم، وموسيقى تركيز تتكيف مع كل مرحلة من جلستك.',
        },
        {
          question: 'ما أنواع الموسيقى التي يقدمها برافوس؟',
          answer:
            'يتعاون برافوس مع فنانين مستقلين لإنشاء موسيقى تركيز متنوعة عبر أنواع متعددة: Future Garage و Chillstep (Neural Drift، Cognitive Bloom، Vibe Coding)، Ambient Piano و Lo-Fi (Scripted Light، Soulful Lounge)، Felt Piano و Minimalist Classical (Deep Piano Focus، Relaxed Classical)، Sacred Ambient (Meditative Ambient)، و Sufi Lo-Fi مع الآلات التقليدية. كل ألبوم رحلة تركيز فريدة لمدة 25 دقيقة مصممة خصيصًا لجلسات بومودورو. تضاف ألبومات جديدة أسبوعيًا.',
        },
        {
          question: 'هل يمكنني استخدام برافوس دون اتصال؟',
          answer:
            'يتطلب تطبيق الويب حاليًا اتصالاً بالإنترنت للوظائف الكاملة. ومع ذلك، نعمل بنشاط على وضع دون اتصال لمستخدمي برو، مما سيسمح لك بتنزيل ألبومات موسيقى التركيز والوصول إلى قائمة مهامك بدون اتصال. الوضع دون اتصال قادم قريبًا. تطبيقات الهاتف المحمول الأصلية مع دعم كامل دون اتصال قيد التطوير أيضًا.',
        },
        {
          question: 'هل يتكامل برافوس مع تطبيقات الإنتاجية الأخرى؟',
          answer:
            'حاليًا، يعمل برافوس كمساحة عمل مستقلة مصممة لاستبدال أدوات متجزأة متعددة. نقوم ببناء تكاملات مع تطبيقات الإنتاجية الشعبية بما في ذلك Notion و Todoist و Google Calendar—قادمة قريبًا. فلسفتنا هي "كل شيء في مكان واحد"، لكننا ندرك أن بعض سير العمل يتطلب ربط الأدوات.',
        },
        {
          question: 'ما هي سياسة الاسترداد الخاصة بكم؟',
          answer:
            'نقدم ضمان استرداد الأموال لمدة 30 يومًا على جميع اشتراكات برو. إذا لم يساعدك برافوس على التركيز بشكل أفضل، سنعيد لك أموالك—بدون أسئلة.',
        },
        {
          question: 'كم يكلف برافوس؟',
          answer:
            'يقدم برافوس مستوى مجاني سخي للأبد مع الميزات الأساسية: 3 جلسات تركيز يوميًا، مؤقت بومودورو أساسي، قائمة مهام بسيطة، والوصول إلى قوائم تشغيل التركيز الأساسية. تبدأ خطط برو من 2.99$/شهر أو 28$/سنة (وفر 22%). يتضمن برو جلسات غير محدودة، مكتبة موسيقى بريميوم، صوت عالي الدقة (WAV بدون فقدان)، إدارة مهام متقدمة، تحليلات كاملة، ودعم ذو أولوية. لا رسوم مفاجئة—نذكرك قبل التجديد.',
        },
        {
          question: 'هل يوجد تطبيق للهاتف المحمول؟',
          answer:
            'حاليًا برافوس متاح كتطبيق ويب يعمل على جميع الأجهزة. تطبيقات iOS و Android الأصلية قيد التطوير وقادمة قريبًا!',
        },
      ],
    },
    cta: {
      title: {
        part1: 'مساحة عمل واحدة.',
        part2: 'تركيز نقي.',
      },
      subtitle:
        'كل ما تحتاجه للتركيز في مساحة عمل واحدة جميلة: المؤقت والمهام والموسيقى. لا مزيد من التطبيقات المبعثرة. لا مزيد من العقل المشتت.',
      cta: 'ابدأ الآن',
      watchDemo: 'شاهد العرض التوضيحي',
      demoModal: {
        title: 'جولة 60 ثانية',
        description:
          'شاهد كيف يساعدك برافوس على تحقيق تركيز عميق في أقل من دقيقة.',
        comingSoon: 'فيديو توضيحي قادم قريبًا!',
      },
    },
    footer: {
      tagline: 'برافوس',
      flowState: 'نظام التشغيل لحالة التركيز',
      etymology: 'من السنسكريتية pravāha: تركيز مستمر',
      sections: {
        product: {
          title: 'المنتج',
          links: {
            about: 'عن البرنامج',
            pricing: 'الأسعار',
            faq: 'الأسئلة الشائعة',
            blog: 'المدونة',
            roadmap: 'خارطة الطريق',
          },
        },
        legal: {
          title: 'القانوني',
          links: {
            privacy: 'سياسة الخصوصية',
            terms: 'شروط الخدمة',
            refund: 'سياسة الاسترداد',
          },
        },
        contact: {
          title: 'اتصل بنا',
          links: {
            support: 'الدعم',
            press: 'الصحافة',
            partnerships: 'الشراكات',
          },
        },
      },
      copyright: '© 2025 برافوس. جميع الحقوق محفوظة.',
    },
  },
};
